import React, { useState } from 'react';
import { Calendar, Clock, Video, Phone, MapPin, Send, Plus, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const API = '/api';

const INTERVIEW_TYPES = [
  { value: 'online', label: 'Online Meeting', icon: Video, description: 'Video call via Zoom, Teams, etc.' },
  { value: 'phone', label: 'Phone Call', icon: Phone, description: 'Voice call' },
  { value: 'onsite', label: 'On-site', icon: MapPin, description: 'In-person at office location' },
];

export const InterviewScheduleDialog = ({
  open,
  onOpenChange,
  applicationId,
  candidateName,
  candidateEmail,
  jobTitle,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [interviewType, setInterviewType] = useState('online');
  
  // Time slot 1 (required)
  const [slot1Date, setSlot1Date] = useState('');
  const [slot1Time, setSlot1Time] = useState('');
  
  // Time slot 2 (optional)
  const [showSlot2, setShowSlot2] = useState(false);
  const [slot2Date, setSlot2Date] = useState('');
  const [slot2Time, setSlot2Time] = useState('');
  
  // Type-specific fields
  const [meetingLink, setMeetingLink] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');

  const resetForm = () => {
    setInterviewType('online');
    setSlot1Date('');
    setSlot1Time('');
    setShowSlot2(false);
    setSlot2Date('');
    setSlot2Time('');
    setMeetingLink('');
    setLocation('');
    setNotes('');
  };

  const handleSend = async () => {
    // Validation
    if (!slot1Date || !slot1Time) {
      toast.error('Please provide at least one interview time option');
      return;
    }

    if (interviewType === 'online' && !meetingLink) {
      toast.error('Please provide a meeting link for online interviews');
      return;
    }

    if (interviewType === 'onsite' && !location) {
      toast.error('Please provide a location for on-site interviews');
      return;
    }

    // Build slots array
    const proposedSlots = [
      { date: slot1Date, time: slot1Time, label: 'Option 1' }
    ];

    if (showSlot2 && slot2Date && slot2Time) {
      proposedSlots.push({ date: slot2Date, time: slot2Time, label: 'Option 2' });
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/applications/${applicationId}/schedule-interview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          interview_type: interviewType,
          proposed_slots: proposedSlots,
          meeting_link: interviewType === 'online' ? meetingLink : null,
          location: interviewType === 'onsite' ? location : null,
          notes: notes || null,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to send interview invitation');
      }

      const result = await response.json();
      toast.success('Interview invitation sent successfully');
      
      if (result.new_stage) {
        toast.info(`Application status updated to: Interview Scheduled`);
      }

      onOpenChange(false);
      resetForm();
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const selectedType = INTERVIEW_TYPES.find(t => t.value === interviewType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[#33AADD]" />
            Schedule Interview
          </DialogTitle>
          <DialogDescription>
            Send an interview invitation to {candidateName} for {jobTitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Interview Type */}
          <div>
            <Label className="text-sm font-medium">Interview Type</Label>
            <Select value={interviewType} onValueChange={setInterviewType}>
              <SelectTrigger className="mt-1.5" data-testid="interview-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INTERVIEW_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="h-4 w-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">{selectedType?.description}</p>
          </div>

          {/* Time Slot 1 (Required) */}
          <div className="p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50">
            <Label className="text-sm font-semibold text-[#111827] flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#33AADD]" />
              Interview Option 1 <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Label htmlFor="slot1Date" className="text-xs text-gray-500">Date</Label>
                <Input
                  id="slot1Date"
                  type="date"
                  value={slot1Date}
                  onChange={(e) => setSlot1Date(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="slot1-date"
                />
              </div>
              <div>
                <Label htmlFor="slot1Time" className="text-xs text-gray-500">Time</Label>
                <Input
                  id="slot1Time"
                  type="time"
                  value={slot1Time}
                  onChange={(e) => setSlot1Time(e.target.value)}
                  data-testid="slot1-time"
                />
              </div>
            </div>
            {slot1Date && slot1Time && (
              <p className="text-xs text-[#33AADD] mt-2 font-medium">
                {formatDateForDisplay(slot1Date)} at {slot1Time}
              </p>
            )}
          </div>

          {/* Time Slot 2 (Optional) */}
          {!showSlot2 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowSlot2(true)}
              className="w-full border-dashed"
              data-testid="add-slot2-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Second Time Option (Optional)
            </Button>
          ) : (
            <div className="p-4 border rounded-lg bg-gray-50 relative">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowSlot2(false);
                  setSlot2Date('');
                  setSlot2Time('');
                }}
                className="absolute top-2 right-2 h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
              <Label className="text-sm font-semibold text-[#111827] flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                Interview Option 2 (Optional)
              </Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label htmlFor="slot2Date" className="text-xs text-gray-500">Date</Label>
                  <Input
                    id="slot2Date"
                    type="date"
                    value={slot2Date}
                    onChange={(e) => setSlot2Date(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="slot2-date"
                  />
                </div>
                <div>
                  <Label htmlFor="slot2Time" className="text-xs text-gray-500">Time</Label>
                  <Input
                    id="slot2Time"
                    type="time"
                    value={slot2Time}
                    onChange={(e) => setSlot2Time(e.target.value)}
                    data-testid="slot2-time"
                  />
                </div>
              </div>
              {slot2Date && slot2Time && (
                <p className="text-xs text-gray-600 mt-2 font-medium">
                  {formatDateForDisplay(slot2Date)} at {slot2Time}
                </p>
              )}
            </div>
          )}

          {/* Meeting Link (for Online) */}
          {interviewType === 'online' && (
            <div>
              <Label htmlFor="meetingLink" className="flex items-center gap-2">
                <Video className="h-4 w-4 text-[#33AADD]" />
                Meeting Link <span className="text-red-500">*</span>
              </Label>
              <Input
                id="meetingLink"
                type="url"
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                placeholder="https://zoom.us/j/... or https://teams.microsoft.com/..."
                className="mt-1.5"
                data-testid="meeting-link-input"
              />
            </div>
          )}

          {/* Location (for Onsite) */}
          {interviewType === 'onsite' && (
            <div>
              <Label htmlFor="location" className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#33AADD]" />
                Interview Location <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Full address including building name, floor, room, etc."
                rows={2}
                className="mt-1.5"
                data-testid="location-input"
              />
            </div>
          )}

          {/* Additional Notes */}
          <div>
            <Label htmlFor="notes">Additional Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional information for the candidate (dress code, parking, documents to bring, etc.)"
              rows={3}
              className="mt-1.5"
              data-testid="interview-notes"
            />
          </div>

          {/* Info Box */}
          <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700 border border-blue-100">
            <p className="font-medium mb-1">How it works:</p>
            <ul className="text-xs space-y-1 text-blue-600">
              <li>• Candidate receives email with time option(s)</li>
              <li>• They can confirm one option or request a different time</li>
              <li>• Application status updates automatically based on response</li>
              <li>• You'll receive email notification when they respond</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={loading}
            className="bg-[#33AADD] hover:bg-[#2890BD]"
            data-testid="send-interview-invitation-btn"
          >
            {loading ? (
              'Sending...'
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Interview Invitation
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InterviewScheduleDialog;
